'use client';

import { useCallback, useEffect } from 'react';
import {
  CliReplayProvider as DesignSystemCliReplayProvider,
  type CliReplayData,
} from '@terra/ui-design-system';
import { useTerminalExecution } from '../../hooks/useTerminalExecution';
import { useBottomPanelStore } from '../../store/bottomPanelStore';
import {
  findTerminalTabForTarget,
  useTerminalStore,
  type TerminalTabTarget,
} from '../../store/terminalStore';
import { primeHostInventory } from '../../utils/host-inventory-cache';

export function CliReplayProvider({ children }: { children: React.ReactNode }) {
  const { ensureTerminalSession, isElectronAvailable } = useTerminalExecution();

  useEffect(() => {
    if (isElectronAvailable) {
      void primeHostInventory();
    }
  }, [isElectronAvailable]);

  const handleSendToTerminal = useCallback(
    (data: CliReplayData) => {
      if (!isElectronAvailable) {
        return;
      }

      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }

      const activeTerminalTab =
        useTerminalStore.getState().terminalTabs.find((tab) => tab.isActive) ??
        null;
      const sessionId = activeTerminalTab?.sessionId ?? 'cli-replay';

      useTerminalStore.getState().getOrCreateTerminalTab(sessionId, {
        cwd: data.workingDirectory,
        shell: activeTerminalTab?.shell,
      });
      useTerminalStore.getState().setActiveTerminalTab(sessionId);
      useBottomPanelStore.getState().setTab('terminal');

      void (async () => {
        const { terminalId } = await ensureTerminalSession({
          sessionId,
          cwd: data.workingDirectory,
          shell: activeTerminalTab?.shell,
        });

        await window.electronAPI.terminal.write(terminalId, data.command);
        useTerminalStore.getState().requestTerminalFocus();
      })().catch((error: unknown) => {
        console.warn('[CliReplay] Failed to send command to terminal', error);
      });
    },
    [ensureTerminalSession, isElectronAvailable],
  );

  const handleOpenTerminal = useCallback((target: TerminalTabTarget) => {
    const terminalStore = useTerminalStore.getState();
    const tab = findTerminalTabForTarget({
      terminalTabs: terminalStore.terminalTabs,
      toolCallTabIds: terminalStore.toolCallTabIds,
      target,
    });

    if (tab) {
      terminalStore.setActiveTerminalTab(tab.id);
    }

    useBottomPanelStore.getState().setTab('terminal');
    terminalStore.requestTerminalFocus();
  }, []);

  return (
    <DesignSystemCliReplayProvider
      onSendToTerminal={isElectronAvailable ? handleSendToTerminal : undefined}
      onOpenTerminal={isElectronAvailable ? handleOpenTerminal : undefined}
    >
      {children}
    </DesignSystemCliReplayProvider>
  );
}
